import { pb } from './pocketbase';

export const getFormations = async () => {
    try {
        const record = await pb.collection('formations').getFullList({
            sort: 'exact_title_of_qualification',
            filter: 'status = "Active"'
        });
        return record;
    } catch (error) {
        console.error('Error fetching formations:', error);
        return [];
    }
};

export const getFormationOptions = (formations, includeIdInLabel = false, valueIsLabel = false) => {
    if (!formations || !Array.isArray(formations)) return [];
    
    if (valueIsLabel) {
        return formations.map(formation => formation.title);
    }
    
    const formationMap = {};
    formations.forEach(formation => {
        const label = includeIdInLabel ? `${formation.qualification_title} (${formation.id})` : formation.qualification_title;
        formationMap[formation.id] = label;
    });
    
    return formationMap;
};

export const searchFormations = async (query) => {
    try {
        if (!query || query.length < 2) {
            return [];
        }
        
        // Recherche par titre avec expansion de la relation formation
        const result = await pb.collection('formations').getList(1, 10, {
            filter: `qualification_title ~ "${query}"`,
            expand: 'formations'
        });
        
        return result.items;
    } catch (error) {
        console.error('Erreur lors de la recherche des étudiants:', error);
        return [];
    }
};

export const getFormationById = async (recordId, options = {}) => {
    try {
        const record = await pb.collection('formations').getOne(recordId, options);
        return record;
    } catch (error) {
        console.error('Erreur lors de la récupération de la formation:', error);
        throw error;
    }
};

export const getFormationList = async (options = {}) => {
    try {
        // Préparation des options de requête pour les formations
        const queryOptions = {
            sort: options.sort || '-created',
            filter: options.filter || '',
            page: options.page || 1,
            perPage: options.perPage || 20
        };
        
        // Préparation des options pour PocketBase
        const pbOptions = {
            sort: queryOptions.sort,
            filter: queryOptions.filter
        };
        
        // Définir les champs à récupérer pour les formations
        if (options.fields) {
            pbOptions.fields = options.fields;
        }
        
        // Récupération des formations
        const result = await pb.collection('formations').getList(
            queryOptions.page,
            queryOptions.perPage,
            pbOptions
        );
        
        // Retourner les résultats déjà formatés par PocketBase
        return {
            items: result.items,
            totalItems: result.totalItems,
            totalPages: result.totalPages,
            page: result.page,
            perPage: queryOptions.perPage
        };
    } catch (error) {
        console.error('Erreur lors de la récupération de la liste des formations:', error);
        
        // En cas d'erreur, retourner une liste vide dans le format attendu
        return {
            items: [],
            totalItems: 0,
            page: 1,
            perPage: 20,
            totalPages: 0
        };
    }
};

export const createFormation = async (formationData) => {
    try {
        if (!pb.authStore.isValid || !pb.authStore.model) {
            throw new Error('Aucun utilisateur authentifié. Impossible de créer la formation sans partner_id.');
        }

        // Préparer les données avec les conversions nécessaires
        const processedData = {
            ...formationData,
            partner_id: pb.authStore.record.partner_id,
            status: "Active"
        };

        // Mapping OPCO: s'assurer que type_diplome est défini depuis degre_diplome
        if (processedData.degre_diplome && !processedData.type_diplome) {
            processedData.type_diplome = processedData.degre_diplome;
        }

        // Convertir les champs numériques
        if (processedData.course_duration) {
            processedData.course_duration = parseInt(processedData.course_duration, 10);
        }
        if (processedData.number_of_hoursinstudy) {
            processedData.number_of_hoursinstudy = parseInt(processedData.number_of_hoursinstudy, 10);
        }
        if (processedData.cost_diplome) {
            processedData.cost_diplome = parseFloat(processedData.cost_diplome);
        }
        if (processedData.total_cost_diplome) {
            processedData.total_cost_diplome = parseFloat(processedData.total_cost_diplome);
        }
        if (processedData.training_department) {
            processedData.training_department = parseInt(processedData.training_department, 10);
        }

        // Gérer l'adresse de formation
        // Si place_Identical_training est false, on garde les champs d'adresse
        // Si place_Identical_training est true, l'adresse du centre sera utilisée automatiquement

        // Gérer le fichier de convention
        const formData = new FormData();
        
        // Ajouter tous les champs de données
        Object.keys(processedData).forEach(key => {
            if (key !== 'convention_file' && processedData[key] !== null && processedData[key] !== undefined) {
                formData.append(key, processedData[key]);
            }
        });
        
        // Ajouter le fichier de convention si présent
        if (processedData.convention_file && processedData.convention_file instanceof File) {
            formData.append('convention_model', processedData.convention_file);
        }

        console.log("Données de formation traitées:", processedData);
        console.log("FormData préparée pour l'envoi");
        
        // Log des champs spécifiques pour debug
        if (processedData.convention_model) {
            console.log("Modèle de convention:", processedData.convention_model);
        }
        if (processedData.convention_file) {
            console.log("Fichier de convention:", processedData.convention_file.name);
        }
        
        const record = await pb.collection('formations').create(formData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la création de la formation:', error);
        throw error;
    }
};

export const updateFormation = async (id, formationData) => {
    try {
        // Préparer les données avec les conversions nécessaires
        const processedData = { ...formationData };

        // Mapping OPCO: s'assurer que type_diplome est défini depuis degre_diplome
        if (processedData.degre_diplome && !processedData.type_diplome) {
            processedData.type_diplome = processedData.degre_diplome;
        }

        // Gérer le fichier de convention
        const formData = new FormData();
        
        // Ajouter tous les champs de données
        Object.keys(processedData).forEach(key => {
            if (key !== 'convention_file') {
                // Pour les champs de fichier, envoyer une chaîne vide pour supprimer
                if (key === 'convention_model' && (processedData[key] === null || processedData[key] === undefined)) {
                    formData.append(key, '');
                } else if (processedData[key] !== null && processedData[key] !== undefined) {
                    formData.append(key, processedData[key]);
                }
            }
        });
        
        // Ajouter le fichier de convention si présent
        if (processedData.convention_file && processedData.convention_file instanceof File) {
            formData.append('convention_model', processedData.convention_file);
        }

        console.log("Données de formation traitées pour mise à jour:", processedData);
        console.log("FormData préparée pour l'envoi");
        
        // Log des champs spécifiques pour debug
        if (processedData.convention_file) {
            console.log("Fichier de convention:", processedData.convention_file.name);
        }
        if (processedData.convention_model === null) {
            console.log("🗑️ Suppression du fichier convention_model demandée - envoi d'une chaîne vide");
        }
        
        const record = await pb.collection('formations').update(id, formData);
        return record;
    } catch (error) {
        console.error('Erreur lors de la mise à jour de la formation:', error);
        throw error;
    }
};

export const deleteFormation = async (id) => {
    try {
        await pb.collection('formations').delete(id);
        return true;
    } catch (error) {
        console.error('Erreur lors de la suppression de la formation:', error);
        throw error;
    }
};

export const duplicateFormation = async (formationData) => {
    try {
        // Supprimer les champs qui ne doivent pas être dupliqués
        const { id, created, updated, ...dataToCreate } = formationData;
        
        // Modifier le titre pour indiquer qu'il s'agit d'une copie
        if (dataToCreate.qualification_title) {
            dataToCreate.qualification_title = `${dataToCreate.qualification_title} (copie)`;
        }
        
        // Créer la nouvelle formation
        const record = await pb.collection('formations').create(dataToCreate);
        return record;
    } catch (error) {
        console.error('Erreur lors de la duplication de la formation:', error);
        throw error;
    }
};
